<template>
    <div class="container-fluid default-dashboard">
        <div class="card">
            <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5>Interaction List</h5>
                    <button class="btn btn-primary" @click="openModal()">
                        <i class="fa fa-plus"></i> Add Interaction
                    </button>
                </div>
            </div>
            <div class="card-body">
                <DataTable
                    :headers="headers"
                    :items="interactions"
                    :loading="loading"
                    :total="total"
                    :params="params"
                    @update:params="updateParams"
                >
                    <template #item.actions="{ item }">
                        <button class="btn btn-sm btn-info me-2" @click="viewInteraction(item as Interaction)">
                            <i class="fa fa-eye"></i>
                        </button>
                        <button class="btn btn-sm btn-warning me-2" @click="editInteraction(item as Interaction)">
                            <i class="fa fa-edit"></i>
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteInteraction(item as Interaction)">
                            <i class="fa fa-trash"></i>
                        </button>
                    </template>
                </DataTable>
            </div>
        </div>
    </div>

    <div class="modal fade bd-example-modal-lg" id="interactionModal" tabindex="-1" ref="modalRef" aria-labelledby="interactionModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <h3 class="modal-header justify-content-center border-0">{{ modalTitle }}</h3>
                    <div class="modal-body">
                        <div class="row">
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Code</label>
                                <input v-model="form.code" type="text" class="form-control" required>
                            </div>
                            <div class="col-md-6 mb-2">
                                <label class="form-label col-form-label">Name</label>
                                <input v-model="form.name" type="text" class="form-control" required>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-danger" type="button" @click="closeModal">Cancel</button>
                        <button class="btn btn-primary" type="submit" @click="saveInteraction">Save</button>
                    </div>
            </div>
        </div>
    </div>

    <div class="modal fade" id="viewInteractionModal" tabindex="-1" ref="viewModalRef">
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Interaction</h5>
                    <button type="button" class="btn-close" @click="closeViewModal"></button>
                </div>
                <div class="modal-body">
                    <div v-if="selectedInteraction">
                        <div class="mb-2"><strong>Code:</strong> {{ selectedInteraction.code }}</div>
                        <div class="mb-2"><strong>Name:</strong> {{ selectedInteraction.name }}</div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeViewModal">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted } from 'vue'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import DataTable from '@/components/table/Datatable.vue'
import { Modal } from 'bootstrap'

const interactions = ref<Interaction[]>([])
const loading = ref(false)
const total = ref(0)
const modalTitle = ref('')
const params = reactive<DataTableParams>({
    page: 1,
    size: 10,
    search: ''
})

const headers = [
    { text: 'Code', value: 'code' },
    { text: 'Name', value: 'name' },
    { text: 'Actions', value: 'actions' }
]

const initForm = {
    ID: null,
    code: '',
    name: ''
}

const form = reactive({
    ...initForm
})

const dataInteraction: Interaction[] = []
import * as master from "@/api/services/MasterRequest";

const fetchInteraction = async () => {
    loading.value = true
    try {
        const resp = await master.postRequest("/interaction/list", {...params});
        dataInteraction.splice(0, dataInteraction.length);
        dataInteraction.push(...resp.data);

        let filteredInteraction = [...dataInteraction]
        if (params.search) {
            const searchLower = params.search.toLowerCase()
            filteredInteraction = dataInteraction.filter(interaction =>
            interaction.code.toLowerCase().includes(searchLower) ||
            interaction.name.toLowerCase().includes(searchLower)
            )
        }

        const start = (params.page - 1) * params.size
        const end = start + params.size
        interactions.value = filteredInteraction.slice(start, end)
        total.value = filteredInteraction.length

    } catch (error) {
        toast.error('Failed to fetch interactions')
    }
    loading.value = false
}

const updateParams = (newParams: Partial<DataTableParams>) => {
    Object.assign(params, newParams)
    fetchInteraction()
}

const modalRef = ref<HTMLElement | null>(null)
let bsModal: any | null = null

const viewModalRef = ref<HTMLElement | null>(null)
let bsViewModal: any | null = null
const selectedInteraction = ref<Bank | null>(null)

onMounted(() => {
    if (modalRef.value) {
        bsModal = new Modal(modalRef.value)
    }
    if (viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
})

const closeModal = () => {
    if (bsModal) {
        bsModal.hide()
    }
}

const editInteraction = (interaction: Interaction) => {
    openModal(interaction);
}

const saveInteraction = async () => {
    loading.value = true
    try {
        if (form.ID) {
            await master.postRequest("/interaction/update", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Interaction updated successfully');
                    closeModal();
                    fetchInteraction();
                } else {
                    toast.error('Failed to update Interaction');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to update Interaction');
            })
        } else {
            await master.postRequest("/interaction/create", form).then((resp) => {
                if (resp.status === 200) {
                    toast.success('Interaction created successfully');
                    closeModal();
                    fetchInteraction();
                } else {
                    toast.error('Failed to create interaction');
                }
            }).catch((error) => {
                toast.error(error.message || 'Failed to create interaction');
            })
        }
    } catch (error: any) {
        toast.error(error.message || 'Failed to save interaction');
    } finally {
        loading.value = false
    }
}

const deleteInteraction = async (interaction: Interaction) => {
    if (!confirm('Are you sure you want to delete this interaction?')) return

    loading.value = true
    try {
        await master.postRequest("/interaction/delete", {ID: interaction.ID}).then((resp) => {
            if (resp.status === 200) {
                toast.success('Interaction deleted successfully');
                closeModal();
                fetchInteraction();
            } else {
                toast.error('Failed to delete interaction');
            }
        })

    } catch (error: any) {
        toast.error(error.message || 'Failed to delete interaction');
    } finally {
        loading.value = false
    }
}


const openModal = (interaction: Interaction | null = null) => {
    if (interaction) {
        modalTitle.value = 'Edit Interaction'
        Object.assign(form, interaction);
    } else {
        modalTitle.value = 'Create Interaction'
        Object.assign(form, {...initForm})
    }
    if (bsModal) {
        bsModal.show()
    }
}

const viewInteraction = (interaction: Interaction) => {
    selectedInteraction.value = interaction
    if (!bsViewModal && viewModalRef.value) {
        bsViewModal = new Modal(viewModalRef.value)
    }
    if (bsViewModal) {
        bsViewModal.show()
    }
}

const closeViewModal = () => {
    if (bsViewModal) {
        bsViewModal.hide()
    }
    selectedInteraction.value = null
}
</script>